import axios from 'axios';
import config from '../config';
import {
    getHostedDashboardServiceHost,
    isDashboardLocalDebugMode,
    isLocalLikeHost
} from '../utils/environmentMode';

const isJwtLikeToken = token => typeof token === 'string' && token.split('.').length === 3;

const getPreferredAuthToken = () => {
    const accessToken = localStorage.getItem('access_token') || '';
    const idToken = localStorage.getItem('id_token') || '';

    // Backend APIs expect access-token audience claims; prefer access_token when present.
    if (isJwtLikeToken(accessToken)) {
        return accessToken;
    }

    if (isJwtLikeToken(idToken)) {
        return idToken;
    }

    return accessToken || idToken || '';
};

const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + getPreferredAuthToken()
});

const createResponseError = (message, status, data) => Object.assign(new Error(message), {
    response: {
        status,
        data,
    },
});

const getUsersHost = () => {
    return config.config.api_users.admin.usersHost;
};

const getCanonicalUsersHost = () => {
    const shouldUseProductionHostedFallback = isDashboardLocalDebugMode() && process.env.REACT_APP_LOCAL_DEBUG_HOSTED_STAGE === 'production';

    if (shouldUseProductionHostedFallback) {
        return 'https://api-users.gophr.app';
    }

    return getHostedDashboardServiceHost({
        developmentHost: 'https://dev-api-users.gophr.app',
        productionHost: 'https://api-users.gophr.app'
    });
};

const getCanonicalPaymentsHost = () => {
    const shouldUseProductionHostedFallback = isDashboardLocalDebugMode() && process.env.REACT_APP_LOCAL_DEBUG_HOSTED_STAGE === 'production';

    if (shouldUseProductionHostedFallback) {
        return 'https://api-payments.gophr.app';
    }

    return getHostedDashboardServiceHost({
        developmentHost: 'https://dev-api-payments.gophr.app',
        productionHost: 'https://api-payments.gophr.app'
    });
};

const getPaymentsHost = () => {
    return config.config.api_payments.host;
};

export const fetchAccountingConnectionStatus = async () => {
    const response = await axios.get(`${getCanonicalPaymentsHost()}/accounting/status`, {
        headers: getAuthHeaders(),
        timeout: 15000,
    });

    return response.data;
};

export const fetchAccountingSummary = async ({ month, startDate, endDate, basis }) => {
    const response = await axios.get(`${getCanonicalPaymentsHost()}/accounting/summary`, {
        headers: getAuthHeaders(),
        params: {
            month,
            start_date: startDate,
            end_date: endDate,
            basis
        },
        timeout: 30000,
    });

    return response.data;
};

// Actual Stripe SaaS subscription revenue (admin). Source of truth for MRR,
// includes legacy subscriptions with no tier metadata.
export const fetchSubscriptionMetrics = async () => {
    const response = await axios.get(`${getCanonicalPaymentsHost()}/v2/subscriptions/metrics`, {
        headers: getAuthHeaders(),
        timeout: 30000,
    });

    return response.data && response.data.payload ? response.data.payload : response.data;
};

export const startQuickBooksAuthorization = async () => {
    const response = await axios.post(`${getCanonicalPaymentsHost()}/accounting/quickbooks/authorize`, {}, {
        headers: getAuthHeaders(),
        timeout: 15000,
    });
    const authorizeUrl = response && response.data && response.data.payload
        ? response.data.payload.authorize_url
        : null;

    if (!authorizeUrl) {
        throw new Error('QuickBooks authorization URL was not returned');
    }

    window.location.assign(authorizeUrl);
};

const PAYMENT_LOOKUP_TIMEOUT_MS = 5000;

const shouldPreferUsersProxyForPayments = () => {
    const configuredPaymentsHost = getPaymentsHost();

    return Boolean(
        configuredPaymentsHost
        && isLocalLikeHost(configuredPaymentsHost)
    );
};

const getShipmentHost = () => {
    return config.config.api_shipments.merchant.shipmentHost;
};

const getShipmentsHasuraHost = () => {
    return config.config.api_shipments.merchant.hasuraHost;
};

const ADVANCE_IF_PAID_UNSUPPORTED_KEY = 'merchant_prepay_advance_if_paid_unsupported';
const ADVANCE_IF_PAID_UNSUPPORTED_TTL_MS = 10 * 60 * 1000;

const isKnownAdvanceIfPaidUnsupportedHost = () => {
    try {
        const shipmentHost = getShipmentHost();

        if (!shipmentHost) {
            return false;
        }

        const parsedUrl = new URL(shipmentHost);

        return parsedUrl.hostname === 'dev-api-shipments.gophr.app';
    } catch (error) {
        return false;
    }
};

const getKnownAdvanceIfPaidUnsupported = () => {
    try {
        const rawValue = localStorage.getItem(ADVANCE_IF_PAID_UNSUPPORTED_KEY);

        if (!rawValue) {
            return false;
        }

        const cachedAt = Number(rawValue);
        if (!Number.isFinite(cachedAt) || cachedAt <= 0) {
            return false;
        }

        return Date.now() - cachedAt < ADVANCE_IF_PAID_UNSUPPORTED_TTL_MS;
    } catch (error) {
        return false;
    }
};

const markAdvanceIfPaidUnsupported = () => {
    try {
        localStorage.setItem(ADVANCE_IF_PAID_UNSUPPORTED_KEY, String(Date.now()));
    } catch (error) {
        // Ignore localStorage failures.
    }
};

const isRecoverablePaymentLookupError = error => {
    if (!error) {
        return false;
    }

    if (error.response && [400, 401, 403, 404, 408, 409, 429, 500, 502, 503, 504].includes(error.response.status)) {
        return true;
    }

    // Network and transport failures should still allow fallback hosts.
    return Boolean(!error.response && (error.request || error.code || error.message));
};

const getLookupErrorStatus = error => {
    return error && error.response ? error.response.status : 'network';
};

const logRecoverableLookupDebug = (...args) => {
    void args;
};

const getExplicitMerchantUsersHosts = () => {
    const configuredUsersHost = getUsersHost();
    const hostedUsersHost = getCanonicalUsersHost();

    if (isDashboardLocalDebugMode() && isLocalLikeHost(configuredUsersHost)) {
        return [...new Set([configuredUsersHost, hostedUsersHost].filter(Boolean))];
    }

    return [...new Set([configuredUsersHost, hostedUsersHost].filter(Boolean))];
};

const getUsersPaymentLookupConfig = extraConfig => ({
    timeout: PAYMENT_LOOKUP_TIMEOUT_MS,
    ...extraConfig,
});

const shouldContinueUsersHostCascadeForEmptyProfile = ({ profileData, usersHost, usersHosts, hostIndex }) => {
    if (!isDashboardLocalDebugMode()) {
        return false;
    }

    if (!Array.isArray(usersHosts) || hostIndex >= usersHosts.length - 1) {
        return false;
    }

    if (!isLocalLikeHost(usersHost || '')) {
        return false;
    }

    const paymentMethods = profileData && profileData.payload && Array.isArray(profileData.payload.payment_methods)
        ? profileData.payload.payment_methods
        : null;

    return Boolean(paymentMethods && paymentMethods.length === 0);
};

const STORE_PAYMENT_TERMS_CACHE_KEY = 'store_payment_terms_by_id';
const STORE_PAYMENT_TERMS_CACHE_TTL_MS = 5 * 60 * 1000;

const normalizeCachedStoreInvoiceFrequencyEntry = cachedEntry => {
    if (typeof cachedEntry === 'string') {
        return {
            invoiceFrequency: cachedEntry,
            updatedAt: 0,
        };
    }

    if (!cachedEntry || typeof cachedEntry !== 'object') {
        return null;
    }

    return {
        invoiceFrequency: cachedEntry.invoiceFrequency,
        updatedAt: Number(cachedEntry.updatedAt) || 0,
    };
};

const readStorePaymentTermsCache = () => {
    try {
        const raw = localStorage.getItem(STORE_PAYMENT_TERMS_CACHE_KEY);

        if (!raw) {
            return {};
        }

        const parsed = JSON.parse(raw);

        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
        return {};
    }
};

const writeStorePaymentTermsCache = cache => {
    try {
        localStorage.setItem(STORE_PAYMENT_TERMS_CACHE_KEY, JSON.stringify(cache || {}));
    } catch (error) {
        // Ignore localStorage failures; checkout still has live API fallbacks.
    }
};

export const setStoredStoreInvoiceFrequency = (storeId, invoiceFrequency) => {
    if (!storeId) {
        return;
    }

    const normalizedInvoiceFrequency = normalizeInvoiceFrequency(invoiceFrequency);

    if (!normalizedInvoiceFrequency || normalizedInvoiceFrequency === 'n/a') {
        return;
    }

    const nextCache = {
        ...readStorePaymentTermsCache(),
        [storeId]: {
            invoiceFrequency: normalizedInvoiceFrequency,
            updatedAt: Date.now(),
        },
    };

    writeStorePaymentTermsCache(nextCache);
};

export const getStoredStoreInvoiceFrequency = storeId => {
    if (!storeId) {
        return null;
    }

    const cache = readStorePaymentTermsCache();
    const cachedEntry = normalizeCachedStoreInvoiceFrequencyEntry(cache[storeId]);

    if (!cachedEntry) {
        return null;
    }

    if (cachedEntry.updatedAt && Date.now() - cachedEntry.updatedAt > STORE_PAYMENT_TERMS_CACHE_TTL_MS) {
        const nextCache = { ...cache };
        delete nextCache[storeId];
        writeStorePaymentTermsCache(nextCache);
        return null;
    }

    const normalizedInvoiceFrequency = normalizeInvoiceFrequency(cachedEntry.invoiceFrequency);

    if (!normalizedInvoiceFrequency || normalizedInvoiceFrequency === 'n/a') {
        return null;
    }

    return normalizedInvoiceFrequency;
};

const fetchMerchantPaymentProfileFromUsers = auth0Id => {
    const merchantPaymentProfilePath = `/admin/merchant-payment-profile/${encodeURIComponent(auth0Id)}`;
    const usersHosts = getExplicitMerchantUsersHosts();

    return usersHosts.reduce(async (previousAttempt, usersHost, index) => {
        try {
            const result = await previousAttempt;
            void index;
            void usersHost;
            return result;
        } catch (error) {
            const errorStatus = getLookupErrorStatus(error);
            logRecoverableLookupDebug('[MerchantProfileCascade] Host', index, 'previous failed (' + errorStatus + '), trying:', usersHost + merchantPaymentProfilePath);

            if (!isRecoverablePaymentLookupError(error)) {
                throw error;
            }

            const response = await axios.get(
                `${usersHost}${merchantPaymentProfilePath}`,
                getUsersPaymentLookupConfig({
                    headers: getAuthHeaders(),
                    validateStatus: status => status === 200 || status === 403 || status === 404 || status === 500,
                })
            );

            if (response.status !== 200) {
                throw createResponseError(`Merchant profile lookup failed with status ${response.status}`, response.status, response.data);
            }

            const normalizedResponse = normalizePaymentProfileResponse(response.data);
            if (shouldContinueUsersHostCascadeForEmptyProfile({
                profileData: normalizedResponse,
                usersHost,
                usersHosts,
                hostIndex: index,
            })) {
                throw createResponseError('Merchant profile lookup returned empty local profile', 404, response.data);
            }

            return response;
        }
    }, Promise.reject({ response: { status: 404 } }));
};

const fetchCustomerPaymentProfileFromUsers = auth0Id => {
    const customerPaymentProfilePath = `/admin/customer-payment-profile/${encodeURIComponent(auth0Id)}`;
    const usersHosts = getExplicitMerchantUsersHosts();

    return usersHosts.reduce(async (previousAttempt, usersHost, index) => {
        try {
            return await previousAttempt;
        } catch (error) {
            if (!isRecoverablePaymentLookupError(error)) {
                throw error;
            }

            const response = await axios.get(
                `${usersHost}${customerPaymentProfilePath}`,
                getUsersPaymentLookupConfig({
                    headers: getAuthHeaders(),
                    validateStatus: status => status === 200 || status === 403 || status === 404 || status === 500,
                })
            );

            if (response.status !== 200) {
                throw createResponseError(`Customer profile lookup failed with status ${response.status}`, response.status, response.data);
            }

            const normalizedResponse = normalizePaymentProfileResponse(response.data);
            if (shouldContinueUsersHostCascadeForEmptyProfile({
                profileData: normalizedResponse,
                usersHost,
                usersHosts,
                hostIndex: index,
            })) {
                throw createResponseError('Customer profile lookup returned empty local profile', 404, response.data);
            }

            return response;
        }
    }, Promise.reject({ response: { status: 404 } }));
};

const fetchCurrentUserPaymentProfileFromUsers = () => {
    const usersHosts = getExplicitMerchantUsersHosts();

    return usersHosts.reduce(async (previousAttempt, usersHost, index) => {
        try {
            return await previousAttempt;
        } catch (error) {
            if (!isRecoverablePaymentLookupError(error)) {
                throw error;
            }

            const response = await axios.get(
                `${usersHost}/payment/profile`,
                getUsersPaymentLookupConfig({
                    headers: getAuthHeaders(),
                })
            );

            const normalizedResponse = normalizePaymentProfileResponse(response.data);
            if (shouldContinueUsersHostCascadeForEmptyProfile({
                profileData: normalizedResponse,
                usersHost,
                usersHosts,
                hostIndex: index,
            })) {
                throw createResponseError('Current user profile lookup returned empty local profile', 404, response.data);
            }

            return response;
        }
    }, Promise.reject({ response: { status: 404 } }));
};

const hasPaymentMethods = profileResponseData => {
    return Boolean(
        profileResponseData
        && profileResponseData.payload
        && Array.isArray(profileResponseData.payload.payment_methods)
        && profileResponseData.payload.payment_methods.length > 0
    );
};

const fetchMerchantSetupIntentFromUsers = auth0Id => {
    const merchantSetupIntentPath = `/admin/merchant-payment-profile/${encodeURIComponent(auth0Id)}/setup-intent`;
    const usersHosts = getExplicitMerchantUsersHosts();

    return usersHosts.reduce(async (previousAttempt, usersHost) => {
        try {
            return await previousAttempt;
        } catch (error) {
            if (!isRecoverablePaymentLookupError(error)) {
                throw error;
            }

            return axios.get(
                `${usersHost}${merchantSetupIntentPath}`,
                getUsersPaymentLookupConfig({
                    headers: getAuthHeaders(),
                    validateStatus: status => status === 200 || status === 404 || status === 500,
                })
            );
        }
    }, Promise.reject({ response: { status: 404 } }));
};

const fetchMerchantAppSetupIntentFromUsers = () => {
    const merchantAppSetupIntentPath = '/payment/merchant-app-setup-intent';
    const usersHosts = getExplicitMerchantUsersHosts();

    return usersHosts.reduce(async (previousAttempt, usersHost) => {
        try {
            return await previousAttempt;
        } catch (error) {
            if (!isRecoverablePaymentLookupError(error)) {
                throw error;
            }

            return axios.get(
                `${usersHost}${merchantAppSetupIntentPath}`,
                getUsersPaymentLookupConfig({
                    headers: getAuthHeaders(),
                    validateStatus: status => status === 200 || status === 404 || status === 500,
                })
            );
        }
    }, Promise.reject({ response: { status: 404 } }));
};

const fetchCustomerPaymentProfile = async (auth0Id, options = {}) => {
    const shouldUseUsersProxyFallback = options.useUsersProxyFallback !== false;
    const shouldUseMerchantProxyFallback = options.useMerchantProxyFallback !== false;
    const shouldPreferUsersProxy = options.preferUsersProxy === true || shouldPreferUsersProxyForPayments();

    if (shouldPreferUsersProxy && shouldUseUsersProxyFallback) {
        try {
            const proxyResponse = await fetchCustomerPaymentProfileFromUsers(auth0Id);
            const profileData = normalizePaymentProfileResponse(proxyResponse.data);

            if (profileData && profileData.payload && Array.isArray(profileData.payload.payment_methods)) {
                return {
                    data: profileData,
                    status: 200,
                };
            }
        } catch (error) {
            if (!isRecoverablePaymentLookupError(error)) {
                throw error;
            }
        }

        if (!shouldUseMerchantProxyFallback) {
            return null;
        }

        try {
            const merchantProfileResponse = await fetchMerchantPaymentProfileFromUsers(auth0Id);
            const profileData = normalizePaymentProfileResponse(merchantProfileResponse.data);

            if (profileData && profileData.payload && Array.isArray(profileData.payload.payment_methods)) {
                return {
                    data: {
                        ...profileData,
                        payload: {
                            ...profileData.payload,
                            invoice_frequency: normalizeInvoiceFrequency(profileData.payload.invoice_frequency),
                        },
                    },
                    status: 200,
                };
            }
        } catch (error) {
            if (!isRecoverablePaymentLookupError(error)) {
                throw error;
            }
        }

        return null;
    }

    try {
        const response = await axios.get(
            `${getPaymentsHost()}/customer-profiles/${encodeURIComponent(auth0Id)}`,
            {
                headers: getAuthHeaders(),
                validateStatus: status => status === 200 || status === 404,
            }
        );

        if (response.status === 200) {
            return response;
        }
    } catch (error) {
        if (!isRecoverablePaymentLookupError(error)) {
            throw error;
        }
    }

    if (!shouldUseUsersProxyFallback) {
        return null;
    }

    try {
        const proxyResponse = await fetchCustomerPaymentProfileFromUsers(auth0Id);
        const profileData = normalizePaymentProfileResponse(proxyResponse.data);

        if (profileData && profileData.payload && Array.isArray(profileData.payload.payment_methods)) {
            return {
                data: profileData,
                status: 200,
            };
        }
    } catch (error) {
        if (!isRecoverablePaymentLookupError(error)) {
            throw error;
        }
    }

    if (!shouldUseMerchantProxyFallback) {
        return null;
    }

    try {
        const merchantProfileResponse = await fetchMerchantPaymentProfileFromUsers(auth0Id);
        const profileData = normalizePaymentProfileResponse(merchantProfileResponse.data);

        if (profileData && profileData.payload && Array.isArray(profileData.payload.payment_methods)) {
            return {
                data: {
                    ...profileData,
                    payload: {
                        ...profileData.payload,
                        invoice_frequency: normalizeInvoiceFrequency(profileData.payload.invoice_frequency),
                    },
                },
                status: 200,
            };
        }
    } catch (error) {
        if (!isRecoverablePaymentLookupError(error)) {
            throw error;
        }
    }

    return null;
};

const getStorePaymentProfilePath = (storeId, options = {}) => {
    const prefix = options.admin ? '/admin' : '/payment';
    const query = new URLSearchParams();
    const shouldIncludeMerchantFilters = !options.admin || options.includeMerchantFilterInAdmin === true;

    if (shouldIncludeMerchantFilters && options.merchantAuth0Id) {
        query.set('merchant_auth0_id', options.merchantAuth0Id);
    }

    if (shouldIncludeMerchantFilters && Array.isArray(options.merchantAuth0Ids) && options.merchantAuth0Ids.length > 0) {
        query.set('merchant_auth0_ids', options.merchantAuth0Ids.filter(Boolean).join(','));
    }

    const queryString = query.toString();

    if (queryString) {
        return `${prefix}/store-payment-profile/${encodeURIComponent(storeId)}?${queryString}`;
    }

    return `${prefix}/store-payment-profile/${encodeURIComponent(storeId)}`;
};

const fetchStorePaymentProfileFromUsers = (storeId, options = {}) => {
    const storePaymentProfilePath = getStorePaymentProfilePath(storeId, options);
    const usersHosts = getExplicitMerchantUsersHosts();

    logRecoverableLookupDebug('[StoreProfileCascade] Starting for storeId:', storeId, 'path:', storePaymentProfilePath, 'hosts:', usersHosts);

    return usersHosts.reduce(async (previousAttempt, usersHost, index) => {
        try {
            const result = await previousAttempt;
            logRecoverableLookupDebug('[StoreProfileCascade] Host', index, 'previous resolved, skipping', usersHost);
            return result;
        } catch (error) {
            var errorStatus = getLookupErrorStatus(error);
            logRecoverableLookupDebug('[StoreProfileCascade] Host', index, 'previous failed (' + errorStatus + '), trying:', usersHost + storePaymentProfilePath);

            if (!isRecoverablePaymentLookupError(error)) {
                throw error;
            }

            var response = await axios.get(
                `${usersHost}${storePaymentProfilePath}`,
                getUsersPaymentLookupConfig({
                    headers: getAuthHeaders(),
                    validateStatus: status => status === 200 || status === 403 || status === 404 || status === 408 || status === 429 || status === 500 || status === 502 || status === 503 || status === 504,
                })
            );

            if (response.status !== 200) {
                throw createResponseError(`Store profile lookup failed with status ${response.status}`, response.status, response.data);
            }

            logRecoverableLookupDebug('[StoreProfileCascade] Host', index, usersHost, 'SUCCESS, status:', response.status, 'hasPayload:', response.data && response.data.hasPayload);
            return response;
        }
    }, Promise.reject({ response: { status: 404 } }));
};

const getStoreSetupIntentPath = (storeId, options = {}) => {
    const prefix = options.admin ? '/admin' : '/payment';
    const query = new URLSearchParams();
    const shouldIncludeMerchantFilters = !options.admin || options.includeMerchantFilterInAdmin === true;

    if (shouldIncludeMerchantFilters && options.merchantAuth0Id) {
        query.set('merchant_auth0_id', options.merchantAuth0Id);
    }

    if (shouldIncludeMerchantFilters && Array.isArray(options.merchantAuth0Ids) && options.merchantAuth0Ids.length > 0) {
        query.set('merchant_auth0_ids', options.merchantAuth0Ids.filter(Boolean).join(','));
    }

    const queryString = query.toString();

    if (queryString) {
        return `${prefix}/store-payment-profile/${encodeURIComponent(storeId)}/setup-intent?${queryString}`;
    }

    return `${prefix}/store-payment-profile/${encodeURIComponent(storeId)}/setup-intent`;
};

const fetchStoreSetupIntentFromUsers = (storeId, options = {}) => {
    const storeSetupIntentPath = getStoreSetupIntentPath(storeId, options);
    const usersHosts = getExplicitMerchantUsersHosts();

    return usersHosts.reduce(async (previousAttempt, usersHost) => {
        try {
            return await previousAttempt;
        } catch (error) {
            if (!isRecoverablePaymentLookupError(error)) {
                throw error;
            }

            const response = await axios.get(
                `${usersHost}${storeSetupIntentPath}`,
                getUsersPaymentLookupConfig({
                    headers: getAuthHeaders(),
                    validateStatus: status => status === 200 || status === 403 || status === 404 || status === 500,
                })
            );

            if (response.status !== 200) {
                throw createResponseError(`Store setup intent lookup failed with status ${response.status}`, response.status, response.data);
            }

            return response;
        }
    }, Promise.reject({ response: { status: 404 } }));
};

const buildStorePaymentProfileSeed = (storeId, merchantProfileSeed = {}) => {
    const auth0Ids = getCandidateAuth0Ids({
        auth0Id: merchantProfileSeed && merchantProfileSeed.auth0Id,
        auth0Ids: merchantProfileSeed && merchantProfileSeed.auth0Ids,
    });
    const auth0Id = auth0Ids[0] || '';
    const email = merchantProfileSeed && merchantProfileSeed.email ? merchantProfileSeed.email : '';

    if (!storeId || !auth0Id || !email) {
        return null;
    }

    return {
        merchant_auth0_id: auth0Id,
        merchant_auth0_ids: auth0Ids,
        first_name: merchantProfileSeed && merchantProfileSeed.first_name ? merchantProfileSeed.first_name : '',
        last_name: merchantProfileSeed && merchantProfileSeed.last_name ? merchantProfileSeed.last_name : '',
        email,
        billing_email: merchantProfileSeed && merchantProfileSeed.billing_email ? merchantProfileSeed.billing_email : email,
        invoice_frequency: normalizeInvoiceFrequency((merchantProfileSeed && merchantProfileSeed.invoice_frequency) || 'single') || 'single',
        store_id: storeId,
        store_name: merchantProfileSeed && merchantProfileSeed.store_name ? merchantProfileSeed.store_name : '',
    };
};

const ensureAdminStorePaymentProfile = async ({ storeId, merchantProfileSeed }) => {
    const payload = buildStorePaymentProfileSeed(storeId, merchantProfileSeed);

    if (!payload) {
        return null;
    }

    const usersHosts = getExplicitMerchantUsersHosts();
    let lastError = null;

    for (const usersHost of usersHosts) {
        try {
            const response = await axios.put(
                `${usersHost}/admin/store-payment-profile/${encodeURIComponent(storeId)}`,
                payload,
                {
                    headers: getAuthHeaders(),
                    validateStatus: status => status === 200 || status === 400 || status === 403 || status === 404 || status === 500,
                }
            );

            if (response.status === 200) {
                return response.data;
            }

            lastError = createResponseError(
                'Unable to provision store payment profile',
                response.status,
                response.data,
            );
        } catch (error) {
            lastError = error;

            if (!isRecoverablePaymentLookupError(error)) {
                throw error;
            }
        }
    }

    if (lastError) {
        throw lastError;
    }

    return null;
};

export const getCandidateAuth0Ids = (options = {}) => {
    const auth0Ids = [];

    if (Array.isArray(options.auth0Ids)) {
        options.auth0Ids.forEach(auth0Id => {
            if (auth0Id) {
                auth0Ids.push(auth0Id);
            }
        });
    }

    if (options.auth0Id) {
        auth0Ids.push(options.auth0Id);
    }

    if (auth0Ids.length === 0) {
        const currentAuth0Id = getCurrentAuth0Id();

        if (currentAuth0Id) {
            auth0Ids.push(currentAuth0Id);
        }
    }

    return [...new Set(auth0Ids.filter(Boolean))];
};

const shouldUseCurrentUserFallback = auth0Ids => {
    const currentAuth0Id = getCurrentAuth0Id();

    return Boolean(currentAuth0Id) && auth0Ids.includes(currentAuth0Id);
};

export const normalizeInvoiceFrequency = invoiceFrequency => {
    const normalized = String(invoiceFrequency || '').toLowerCase();

    if (!normalized || ['n_a', 'na'].includes(normalized)) {
        return 'n/a';
    }

    if (['prepay', 'single'].includes(normalized)) {
        return 'single';
    }

    if (normalized === 'monthly') {
        return 'net_30';
    }

    if (normalized === '14') {
        return 'net_14';
    }

    if (normalized === '30') {
        return 'net_30';
    }

    if (normalized === '60') {
        return 'net_60';
    }

    if (normalized === '120') {
        return 'net_120';
    }

    return normalized;
};

export const allowsNetTerms = invoiceFrequency => {
    const normalized = normalizeInvoiceFrequency(invoiceFrequency);

    return Boolean(normalized) && !['n/a', 'single', 'ondemand'].includes(normalized);
};

export const getPaymentTermLabel = invoiceFrequency => {
    const normalized = normalizeInvoiceFrequency(invoiceFrequency);

    if (!allowsNetTerms(normalized)) {
        return 'Prepay';
    }

    if (normalized === 'daily') {
        return 'Daily';
    }

    if (normalized === 'weekly') {
        return 'Weekly';
    }

    if (normalized === 'net_14') {
        return 'Net 14';
    }

    if (normalized === 'net_30') {
        return 'Net 30';
    }

    if (normalized === 'net_60') {
        return 'Net 60';
    }

    if (normalized === 'net_120') {
        return 'Net 120';
    }

    return 'Net Terms';
};

export const getPaymentTermSelectValue = invoiceFrequency => {
    const normalized = normalizeInvoiceFrequency(invoiceFrequency);

    return allowsNetTerms(normalized) ? normalized : 'single';
};

export const isKnownPaymentTermValue = invoiceFrequency => {
    return ['single', 'daily', 'weekly', 'net_14', 'net_30', 'net_60', 'net_120'].includes(
        getPaymentTermSelectValue(invoiceFrequency)
    );
};

const normalizePaymentMethod = paymentMethod => {
    if (!paymentMethod || typeof paymentMethod !== 'object') {
        return null;
    }

    if (paymentMethod.gateway_profile_id) {
        return paymentMethod;
    }

    if (!paymentMethod.id || !paymentMethod.type) {
        return paymentMethod;
    }

    if (paymentMethod.type === 'card' && paymentMethod.card) {
        return {
            gateway_profile_id: paymentMethod.id,
            payment_method_type: 'card',
            payment_method_number: `XXXX${paymentMethod.card.last4 || ''}`,
            payment_method_provider: paymentMethod.card.brand || 'card',
            payment_method_exp_date: paymentMethod.card.exp_year && paymentMethod.card.exp_month
                ? `${paymentMethod.card.exp_year}-${String(paymentMethod.card.exp_month).padStart(2, '0')}`
                : null,
        };
    }

    if (paymentMethod.type === 'us_bank_account' && paymentMethod.us_bank_account) {
        return {
            gateway_profile_id: paymentMethod.id,
            payment_method_type: 'us_bank_account',
            payment_method_number: `XXXX${paymentMethod.us_bank_account.last4 || ''}`,
            payment_method_provider: paymentMethod.us_bank_account.bank_name || 'US Bank Account',
            payment_method_exp_date: null,
        };
    }

    if (paymentMethod.type === 'acss_debit' && paymentMethod.acss_debit) {
        return {
            gateway_profile_id: paymentMethod.id,
            payment_method_type: 'acss_debit',
            payment_method_number: `XXXX${paymentMethod.acss_debit.last4 || ''}`,
            payment_method_provider: paymentMethod.acss_debit.bank_name || 'Canadian Bank Account',
            payment_method_exp_date: null,
        };
    }

    if (paymentMethod.type === 'oxxo') {
        return {
            gateway_profile_id: paymentMethod.id,
            payment_method_type: 'oxxo',
            payment_method_number: 'OXXO',
            payment_method_provider: 'OXXO',
            payment_method_exp_date: null,
        };
    }

    return paymentMethod;
};

const normalizePaymentProfileResponse = responseData => {
    if (!responseData || typeof responseData !== 'object' || !responseData.payload || typeof responseData.payload !== 'object') {
        return responseData;
    }

    const payload = responseData.payload;

    return {
        ...responseData,
        payload: {
            ...payload,
            invoice_frequency: normalizeInvoiceFrequency(payload.invoice_frequency),
            payment_methods: Array.isArray(payload.payment_methods)
                ? payload.payment_methods.map(normalizePaymentMethod).filter(Boolean)
                : [],
        },
    };
};

const mergePaymentProfileResponses = (merchantProfileResponse, customerProfileResponse) => {
    const normalizedMerchantProfile = normalizePaymentProfileResponse(merchantProfileResponse);
    const normalizedCustomerProfile = normalizePaymentProfileResponse(customerProfileResponse);

    if (!normalizedMerchantProfile || typeof normalizedMerchantProfile !== 'object') {
        return normalizedCustomerProfile;
    }

    if (!normalizedMerchantProfile.payload || typeof normalizedMerchantProfile.payload !== 'object') {
        return normalizedMerchantProfile;
    }

    const merchantPaymentMethods = Array.isArray(normalizedMerchantProfile.payload.payment_methods)
        ? normalizedMerchantProfile.payload.payment_methods
        : [];
    const customerPaymentMethods = normalizedCustomerProfile && normalizedCustomerProfile.payload && Array.isArray(normalizedCustomerProfile.payload.payment_methods)
        ? normalizedCustomerProfile.payload.payment_methods
        : [];

    if (customerPaymentMethods.length === 0) {
        return normalizedMerchantProfile;
    }

    const mergedPaymentMethods = [...merchantPaymentMethods, ...customerPaymentMethods].filter((paymentMethod, index, collection) => {
        const gatewayProfileId = paymentMethod && paymentMethod.gateway_profile_id;

        if (!gatewayProfileId) {
            return true;
        }

        return collection.findIndex(candidate => candidate && candidate.gateway_profile_id === gatewayProfileId) === index;
    });

    return {
        ...normalizedMerchantProfile,
        payload: {
            ...normalizedMerchantProfile.payload,
            payment_methods: mergedPaymentMethods
        }
    };
};

const enrichMerchantProfileWithCustomerPaymentMethods = async (profileResponse, auth0Id, options = {}) => {
    const normalizedProfileResponse = normalizePaymentProfileResponse(profileResponse);
    const shouldIncludeCustomerPaymentMethods = options.includeCustomerPaymentMethods !== false;

    if (!shouldIncludeCustomerPaymentMethods) {
        return normalizedProfileResponse;
    }

    try {
        const customerProfileResponse = await fetchCustomerPaymentProfile(auth0Id, {
            useUsersProxyFallback: options.customerProfileProxyFallback !== false,
            useMerchantProxyFallback: options.customerProfileProxyFallback !== false,
        });

        if (!customerProfileResponse) {
            return normalizedProfileResponse;
        }

        return mergePaymentProfileResponses(normalizedProfileResponse, customerProfileResponse.data);
    } catch (error) {
        if (!isRecoverablePaymentLookupError(error)) {
            throw error;
        }

        return normalizedProfileResponse;
    }
};

const withResolvedAuth0Id = (responseData, auth0Id) => {
    if (!responseData || typeof responseData !== 'object') {
        return responseData;
    }

    const payload = responseData.payload && typeof responseData.payload === 'object'
        ? {
            ...responseData.payload,
            resolved_auth0_id: auth0Id,
        }
        : responseData.payload;

    return {
        ...responseData,
        resolved_auth0_id: auth0Id,
        payload,
    };
};

export const fetchStorePaymentProfile = async (storeId, options = {}) => {
    if (!storeId) {
        return null;
    }

    const hasExplicitAdminMode = Object.prototype.hasOwnProperty.call(options, 'admin');
    const storeProfileAdminModes = hasExplicitAdminMode ? [Boolean(options.admin)] : [false, true];
    let recoverableError = null;

    logRecoverableLookupDebug('[StoreProfile] Fetching for storeId:', storeId, 'adminModes:', storeProfileAdminModes, 'hasExplicitAdminMode:', hasExplicitAdminMode);

    for (const adminMode of storeProfileAdminModes) {
        try {
            const path = getStorePaymentProfilePath(storeId, { admin: adminMode });
            logRecoverableLookupDebug('[StoreProfile] Trying path:', path, '(admin:', adminMode, ')');
            const response = await fetchStorePaymentProfileFromUsers(storeId, {
                ...options,
                admin: adminMode,
            });

            const responseData = normalizePaymentProfileResponse(response.data);
            const resolvedAuth0Id = responseData && responseData.payload ? (responseData.payload.merchant_auth0_id || null) : null;

            logRecoverableLookupDebug('[StoreProfile] Success! resolvedAuth0Id:', resolvedAuth0Id, 'invoice_frequency:', responseData && responseData.payload && responseData.payload.invoice_frequency, 'payment_methods:', responseData && responseData.payload && Array.isArray(responseData.payload.payment_methods) && responseData.payload.payment_methods.length);
            return withResolvedAuth0Id(responseData, resolvedAuth0Id);
        } catch (error) {
            logRecoverableLookupDebug('[StoreProfile] Recoverable error on admin:', adminMode, '-', getLookupErrorStatus(error));
            if (isRecoverablePaymentLookupError(error)) {
                recoverableError = error;
                // A missing public store profile (404) often means merchant-path visibility gaps,
                // so allow optional admin-path fallback probing for checkout flows.
                if (!hasExplicitAdminMode && getLookupErrorStatus(error) === 404 && options.allowAdminFallbackOn404 !== true) {
                    break;
                }
                continue;
            }

            throw error;
        }
    }

    if (recoverableError) {
        logRecoverableLookupDebug('[StoreProfile] All admin modes failed with recoverable errors');
        return null;
    }

    return null;
};

export const fetchCustomerCardsProfile = async (options = {}) => {
    const auth0Id = options.auth0Id || getCurrentAuth0Id();

    if (!auth0Id) {
        return null;
    }

    try {
        const response = await fetchCustomerPaymentProfile(auth0Id, options);

        if (!response) {
            return null;
        }

        return withResolvedAuth0Id(normalizePaymentProfileResponse(response.data), auth0Id);
    } catch (error) {
        if (isRecoverablePaymentLookupError(error)) {
            return null;
        }

        throw error;
    }
};

export const deleteStoredPaymentMethod = async ({ auth0Id, paymentMethodId }) => {
    if (!auth0Id || !paymentMethodId) {
        throw new Error('Missing auth0Id or paymentMethodId');
    }

    const response = await axios.delete(
        `${getPaymentsHost()}/customer-profiles/${encodeURIComponent(auth0Id)}/payment-methods/${encodeURIComponent(paymentMethodId)}`,
        {
            headers: getAuthHeaders(),
            validateStatus: status => [200, 400, 404, 409, 500].includes(status),
        }
    );

    if (response.status !== 200) {
        const message = response && response.data && response.data.message
            ? response.data.message
            : 'Unable to delete payment method';
        const error = new Error(message);
        error.response = response;
        throw error;
    }

    return response.data;
};

export const fetchCheckoutPaymentProfile = async (options = {}) => {
    const storeId = options.storeId || '';
    const includeCustomerCards = options.includeCustomerCards !== false;
    const hasExplicitCardAuth0Id = Object.prototype.hasOwnProperty.call(options, 'cardAuth0Id');
    const cardAuth0Id = hasExplicitCardAuth0Id ? options.cardAuth0Id : getCurrentAuth0Id();
    const hasStoreSelected = Boolean(storeId);
    const merchantAuth0Ids = getCandidateAuth0Ids({ auth0Id: options.merchantAuth0Id, auth0Ids: options.merchantAuth0Ids });
    const normalizedStoreInvoiceFrequencyOverride = normalizeInvoiceFrequency(options.storeInvoiceFrequencyOverride);
    const cachedStoreInvoiceFrequency = getStoredStoreInvoiceFrequency(storeId);
    const effectiveStoreInvoiceFrequencyOverride = normalizedStoreInvoiceFrequencyOverride && normalizedStoreInvoiceFrequencyOverride !== 'n/a'
        ? normalizedStoreInvoiceFrequencyOverride
        : cachedStoreInvoiceFrequency;
    const shouldLookupStoreProfile = Boolean(storeId)
        && options.storeProfileLookup !== false;

    // Only pass admin mode through when explicitly provided by the caller.
    // Omitting it lets fetchStorePaymentProfile try both /payment/ and /admin/ paths.
    const storeProfileOptions = Object.prototype.hasOwnProperty.call(options, 'admin')
        ? { admin: Boolean(options.admin) }
        : {};

    // Always fetch cards for the checkout card billing identity.
    // Merchant/profile lookups can return invoice-only payloads, so relying on them
    // can hide saved cards in self-checkout scenarios.
    const shouldFetchCustomerCardsProfile = includeCustomerCards && Boolean(cardAuth0Id);

    const [storeProfile, customerCardsProfile] = await Promise.all([
        shouldLookupStoreProfile
            ? fetchStorePaymentProfile(storeId, {
                ...storeProfileOptions,
                merchantAuth0Ids,
                // Checkout should probe the admin path when the public store profile is missing.
                allowAdminFallbackOn404: true,
            }).catch(error => {
                if (isRecoverablePaymentLookupError(error)) {
                    logRecoverableLookupDebug('[Checkout] fetchStorePaymentProfile recoverable error:', getLookupErrorStatus(error));
                    return null;
                }

                throw error;
            })
            : Promise.resolve(null),
        shouldFetchCustomerCardsProfile
            ? fetchCustomerCardsProfile({
                auth0Id: cardAuth0Id,
                useMerchantProxyFallback: false,
            })
            : Promise.resolve(null),
    ]);

    if (storeId && storeProfile && storeProfile.payload && storeProfile.payload.invoice_frequency) {
        setStoredStoreInvoiceFrequency(storeId, storeProfile.payload.invoice_frequency);
    }

    const storeMerchantAuth0Id = storeProfile && storeProfile.payload ? storeProfile.payload.merchant_auth0_id : null;
    const merchantBillingCandidates = getCandidateAuth0Ids({ auth0Ids: [storeMerchantAuth0Id, ...merchantAuth0Ids] });
    const storePayloadForCheckout = storeProfile && storeProfile.payload ? storeProfile.payload : null;
    const storeProfileAlreadyCoversCheckoutBilling = Boolean(
        hasStoreSelected
        && storePayloadForCheckout
        && (
            Object.prototype.hasOwnProperty.call(storePayloadForCheckout, 'invoice_frequency')
            || Boolean(storePayloadForCheckout.billing_email)
            || (Array.isArray(storePayloadForCheckout.payment_methods) && storePayloadForCheckout.payment_methods.length > 0)
        )
    );
    const merchantBillingProfile = merchantBillingCandidates.length > 0
        && !storeProfileAlreadyCoversCheckoutBilling
        ? await fetchPaymentProfile({
            auth0Ids: merchantBillingCandidates,
            includeCustomerPaymentMethods: false,
            // During checkout we already attempt a dedicated customer cards lookup when needed,
            // so skip extra customer-proxy cascades from merchant profile enrichment.
            customerProfileProxyFallback: false,
        }).catch(error => {
            if (isRecoverablePaymentLookupError(error)) {
                return null;
            }

            throw error;
        })
        : null;

    const storePayload = storeProfile && storeProfile.payload
        ? storeProfile.payload
        : (merchantBillingProfile && merchantBillingProfile.payload ? merchantBillingProfile.payload : {});
    const merchantPayload = merchantBillingProfile && merchantBillingProfile.payload ? merchantBillingProfile.payload : {};
    const customerPayload = customerCardsProfile && customerCardsProfile.payload ? customerCardsProfile.payload : {};
    const annotatePaymentMethods = (methods, billingAuth0Id) => {
        if (!Array.isArray(methods)) {
            return [];
        }

        return methods.map(paymentMethod => ({
            ...paymentMethod,
            billing_auth0_id: paymentMethod && paymentMethod.billing_auth0_id ? paymentMethod.billing_auth0_id : billingAuth0Id,
        }));
    };
    const merchantPaymentMethodsSource = storeProfile && storeProfile.payload && Array.isArray(storeProfile.payload.payment_methods)
        ? storeProfile.payload.payment_methods
        : (merchantBillingProfile && merchantBillingProfile.payload && Array.isArray(merchantBillingProfile.payload.payment_methods)
            ? merchantBillingProfile.payload.payment_methods
            : []);
    const merchantPaymentMethodsBillingAuth0Id = (storeProfile && storeProfile.payload && storeProfile.payload.merchant_auth0_id)
        || (storeProfile && storeProfile.resolved_auth0_id)
        || (merchantBillingProfile && merchantBillingProfile.resolved_auth0_id)
        || null;
    const merchantPaymentMethods = annotatePaymentMethods(
        merchantPaymentMethodsSource,
        merchantPaymentMethodsBillingAuth0Id
    );
    const customerPaymentMethods = annotatePaymentMethods(
        Array.isArray(customerPayload.payment_methods) ? customerPayload.payment_methods : [],
        (customerCardsProfile && customerCardsProfile.resolved_auth0_id) || cardAuth0Id || null
    );
    const paymentMethods = [...customerPaymentMethods, ...merchantPaymentMethods].filter((paymentMethod, index, collection) => {
        const gatewayProfileId = paymentMethod && paymentMethod.gateway_profile_id;

        if (!gatewayProfileId) {
            return true;
        }

        return collection.findIndex(candidate => candidate && candidate.gateway_profile_id === gatewayProfileId) === index;
    });
    const resolvedStoreBillingAuth0Id = storePayload.merchant_auth0_id || (storeProfile && storeProfile.resolved_auth0_id) || (merchantBillingProfile && merchantBillingProfile.resolved_auth0_id) || null;
    const resolvedCardBillingAuth0Id = cardAuth0Id || (customerPaymentMethods.length > 0
        ? (customerCardsProfile && customerCardsProfile.resolved_auth0_id) || null
        : (merchantPaymentMethods.length > 0
            ? ((merchantBillingProfile && merchantBillingProfile.resolved_auth0_id) || resolvedStoreBillingAuth0Id || null)
            : (resolvedStoreBillingAuth0Id || (merchantBillingProfile && merchantBillingProfile.resolved_auth0_id) || null)));

    const normalizeInvoiceFrequencyForCheckout = invoiceFrequency => {
        const normalized = normalizeInvoiceFrequency(invoiceFrequency);

        if (!normalized || normalized === 'n/a') {
            return null;
        }

        return normalized;
    };

    const storeInvoiceFrequencyFromStoreProfile = storeProfile && storeProfile.payload
        ? storeProfile.payload.invoice_frequency
        : null;

    const normalizedStoreInvoiceFrequency = normalizeInvoiceFrequencyForCheckout(
        storeInvoiceFrequencyFromStoreProfile || effectiveStoreInvoiceFrequencyOverride
    );
    const normalizedMerchantInvoiceFrequency = normalizeInvoiceFrequencyForCheckout(merchantPayload.invoice_frequency);
    const normalizedCustomerInvoiceFrequency = normalizeInvoiceFrequencyForCheckout(customerPayload.invoice_frequency);
    let preferredInvoiceFrequency = normalizedStoreInvoiceFrequency;
    const canUseMerchantInvoiceFrequencyForStoreSelection = !hasStoreSelected || !shouldLookupStoreProfile || !storeProfile;

    if (canUseMerchantInvoiceFrequencyForStoreSelection && (!preferredInvoiceFrequency || preferredInvoiceFrequency === 'n/a') && normalizedMerchantInvoiceFrequency && normalizedMerchantInvoiceFrequency !== 'n/a') {
        preferredInvoiceFrequency = normalizedMerchantInvoiceFrequency;
    } else if ((!preferredInvoiceFrequency || preferredInvoiceFrequency === 'n/a') && normalizedCustomerInvoiceFrequency && normalizedCustomerInvoiceFrequency !== 'n/a') {
        preferredInvoiceFrequency = normalizedCustomerInvoiceFrequency;
    }

    const resolvedInvoiceFrequency = preferredInvoiceFrequency
        ? normalizeInvoiceFrequency(preferredInvoiceFrequency)
        : (hasStoreSelected ? null : 'single');

    const result = {
        payload: {
            billing_email: storePayload.billing_email || merchantPayload.billing_email || merchantPayload.email || customerPayload.billing_email || customerPayload.email || '',
            invoice_frequency: resolvedInvoiceFrequency,
            payment_methods: paymentMethods,
            merchant_auth0_id: resolvedStoreBillingAuth0Id,
            card_auth0_id: resolvedCardBillingAuth0Id,
        },
        resolved_auth0_id: resolvedStoreBillingAuth0Id,
        net_terms_auth0_id: resolvedStoreBillingAuth0Id,
        card_auth0_id: resolvedCardBillingAuth0Id,
    };

    return result;
};

const decodeJwtPayload = token => {
    if (!token) {
        return null;
    }

    try {
        const payloadSegment = token.split('.')[1];

        if (!payloadSegment) {
            return null;
        }

        const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
        const normalizedBase64 = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
        const json = window.atob(normalizedBase64);

        return JSON.parse(json);
    } catch (error) {
        return null;
    }
};

const splitDisplayName = name => {
    const trimmed = String(name || '').trim();

    if (!trimmed) {
        return {
            first_name: '',
            last_name: '',
        };
    }

    const parts = trimmed.split(/\s+/).filter(Boolean);

    if (parts.length === 1) {
        return {
            first_name: parts[0],
            last_name: parts[0],
        };
    }

    return {
        first_name: parts[0],
        last_name: parts.slice(1).join(' '),
    };
};

const buildMerchantProfileSeed = (auth0Id, overrides = {}) => {
    const payload = decodeJwtPayload(localStorage.getItem('access_token')) || {};
    const displayNameParts = splitDisplayName(overrides.name || payload.name || payload.nickname || '');
    const first_name = overrides.first_name || payload.given_name || displayNameParts.first_name;
    const last_name = overrides.last_name || payload.family_name || displayNameParts.last_name;
    const email = overrides.email || payload.email || '';
    const billing_email = overrides.billing_email || email;

    return {
        user_auth0_id: auth0Id,
        first_name,
        last_name,
        email,
        billing_email,
        invoice_frequency: normalizeInvoiceFrequency(overrides.invoice_frequency || 'single') || 'single',
        store_id: overrides.store_id || '',
        store_name: overrides.store_name || '',
    };
};

const hasProvisionableMerchantSeed = seed => {
    return Boolean(
        seed &&
        seed.user_auth0_id &&
        seed.first_name &&
        seed.last_name &&
        seed.email
    );
};

export const ensureMerchantPaymentProfile = async (options = {}) => {
    const auth0Id = options.auth0Id || getCandidateAuth0Ids(options)[0] || getCurrentAuth0Id();

    if (!auth0Id) {
        throw new Error('Could not determine merchant account id');
    }

    const seed = buildMerchantProfileSeed(auth0Id, options);

    if (!hasProvisionableMerchantSeed(seed)) {
        throw new Error('Merchant payment profile could not be created because the current session is missing name or email information.');
    }

    return axios.post(
        `${getPaymentsHost()}/v2/direct/merchant-profiles`,
        seed,
        {
            headers: getAuthHeaders(),
        }
    );
};

export const fetchMerchantSetupIntent = async (options = {}) => {
    const auth0Ids = getCandidateAuth0Ids(options);
    const currentAuth0Id = getCurrentAuth0Id();
    const shouldPreferUsersProxy = options.preferUsersProxy === true || shouldPreferUsersProxyForPayments();
    const preferredStoreMerchantAuth0Id = getCandidateAuth0Ids({
        auth0Id: options.auth0Id || (options.merchantProfileSeed && options.merchantProfileSeed.auth0Id),
        auth0Ids: [
            ...(Array.isArray(options.auth0Ids) ? options.auth0Ids : []),
            ...(options.merchantProfileSeed && Array.isArray(options.merchantProfileSeed.auth0Ids) ? options.merchantProfileSeed.auth0Ids : []),
        ],
    })[0] || null;

    if (options.storeId) {
        if (options.adminStoreProfileLookup === true || shouldPreferUsersProxy) {
            let storeProfile = await fetchStorePaymentProfile(options.storeId, {
                admin: options.adminStoreProfileLookup,
                allowAdminFallbackOn404: true,
                merchantAuth0Id: preferredStoreMerchantAuth0Id,
            });

            if (!storeProfile && options.adminStoreProfileLookup === true) {
                try {
                    await ensureAdminStorePaymentProfile({
                        storeId: options.storeId,
                        merchantProfileSeed: options.merchantProfileSeed,
                    });
                    storeProfile = await fetchStorePaymentProfile(options.storeId, {
                        admin: options.adminStoreProfileLookup,
                        allowAdminFallbackOn404: true,
                        merchantAuth0Id: preferredStoreMerchantAuth0Id,
                    });
                } catch (provisionError) {
                    if (!isRecoverablePaymentLookupError(provisionError)) {
                        throw provisionError;
                    }
                }
            }
        }

        try {
            const storeSetupIntentResponse = await fetchStoreSetupIntentFromUsers(options.storeId, {
                admin: options.adminStoreProfileLookup,
                merchantAuth0Id: preferredStoreMerchantAuth0Id,
            });
            return storeSetupIntentResponse.data;
        } catch (storeSetupIntentError) {
            if (!isRecoverablePaymentLookupError(storeSetupIntentError)) {
                throw storeSetupIntentError;
            }

            let storeProfile = await fetchStorePaymentProfile(options.storeId, {
                admin: options.adminStoreProfileLookup,
                allowAdminFallbackOn404: true,
                merchantAuth0Id: preferredStoreMerchantAuth0Id,
            });

            if (!storeProfile && options.adminStoreProfileLookup === true) {
                try {
                    await ensureAdminStorePaymentProfile({
                        storeId: options.storeId,
                        merchantProfileSeed: options.merchantProfileSeed,
                    });
                    storeProfile = await fetchStorePaymentProfile(options.storeId, {
                        admin: options.adminStoreProfileLookup,
                        allowAdminFallbackOn404: true,
                        merchantAuth0Id: preferredStoreMerchantAuth0Id,
                    });
                } catch (provisionError) {
                    if (!isRecoverablePaymentLookupError(provisionError)) {
                        throw provisionError;
                    }
                }
            }

            const resolvedStoreMerchantAuth0Id = storeProfile && storeProfile.payload
                ? storeProfile.payload.merchant_auth0_id || null
                : null;

            if (resolvedStoreMerchantAuth0Id) {
                const userProxyResponse = await fetchMerchantSetupIntentFromUsers(resolvedStoreMerchantAuth0Id);
                if (userProxyResponse.status === 200) {
                    return userProxyResponse.data;
                }
            }

            throw storeSetupIntentError;
        }
    }

    if (auth0Ids.length === 0) {
        return null;
    }

    let recoverableError = null;

    for (const auth0Id of auth0Ids) {
        if (shouldPreferUsersProxy) {
            try {
                const userProxyResponse = await fetchMerchantSetupIntentFromUsers(auth0Id);
                if (userProxyResponse.status === 200) {
                    return userProxyResponse.data;
                }

                recoverableError = createResponseError(
                    'Unable to fetch merchant setup intent',
                    userProxyResponse.status,
                    userProxyResponse.data,
                );
            } catch (userProxyError) {
                if (!isRecoverablePaymentLookupError(userProxyError)) {
                    throw userProxyError;
                }

                recoverableError = userProxyError;
            }

            if (auth0Id === currentAuth0Id) {
                try {
                    const response = await fetchMerchantAppSetupIntentFromUsers();
                    if (response.status === 200) {
                        return response.data;
                    }

                    recoverableError = createResponseError(
                        'Unable to fetch merchant app setup intent',
                        response.status,
                        response.data,
                    );
                } catch (fallbackError) {
                    if (!isRecoverablePaymentLookupError(fallbackError)) {
                        throw fallbackError;
                    }

                    recoverableError = fallbackError;
                }
            }

            continue;
        }

        try {
            const response = await axios.get(
                `${getPaymentsHost()}/v2/merchant/${encodeURIComponent(auth0Id)}/setup-intent-secret`,
                {
                    headers: getAuthHeaders(),
                    validateStatus: status => status === 200 || status === 404 || status === 500,
                }
            );

            if (response.status === 200) {
                return response.data;
            }

            recoverableError = createResponseError(
                'Unable to fetch merchant setup intent',
                response.status,
                response.data,
            );

            const userProxyResponse = await fetchMerchantSetupIntentFromUsers(auth0Id);
            if (userProxyResponse.status === 200) {
                return userProxyResponse.data;
            }

            recoverableError = createResponseError(
                'Unable to fetch merchant setup intent',
                userProxyResponse.status,
                userProxyResponse.data,
            );
            continue;
        } catch (error) {
            if (isRecoverablePaymentLookupError(error)) {
                recoverableError = error;

                try {
                    const userProxyResponse = await fetchMerchantSetupIntentFromUsers(auth0Id);
                    if (userProxyResponse.status === 200) {
                        return userProxyResponse.data;
                    }

                    recoverableError = createResponseError(
                        'Unable to fetch merchant setup intent',
                        userProxyResponse.status,
                        userProxyResponse.data,
                    );
                    continue;
                } catch (userProxyError) {
                    if (!isRecoverablePaymentLookupError(userProxyError)) {
                        throw userProxyError;
                    }

                    recoverableError = userProxyError;
                    continue;
                }
            }

            throw error;
        }
    }

    if (shouldUseCurrentUserFallback(auth0Ids)) {
        try {
            const response = await fetchMerchantAppSetupIntentFromUsers();
            if (response.status === 200) {
                return response.data;
            }

            recoverableError = createResponseError(
                'Unable to fetch merchant app setup intent',
                response.status,
                response.data,
            );
        } catch (fallbackError) {
            if (!isRecoverablePaymentLookupError(fallbackError)) {
                throw fallbackError;
            }

            recoverableError = fallbackError;
        }
    }

    if (auth0Ids.length === 1 && options.provisionMerchantProfile !== false) {
        try {
            await ensureMerchantPaymentProfile({
                ...options,
                auth0Id: auth0Ids[0],
            });

            const retryResponse = await axios.get(
                `${getPaymentsHost()}/v2/merchant/${encodeURIComponent(auth0Ids[0])}/setup-intent-secret`,
                {
                    headers: getAuthHeaders(),
                    validateStatus: status => status === 200 || status === 404 || status === 500,
                }
            );

            if (retryResponse.status === 200) {
                return retryResponse.data;
            }

            recoverableError = createResponseError(
                'Unable to fetch merchant setup intent',
                retryResponse.status,
                retryResponse.data,
            );
        } catch (ensureError) {
            if (!isRecoverablePaymentLookupError(ensureError)) {
                throw ensureError;
            }

            recoverableError = ensureError;

            if (shouldUseCurrentUserFallback(auth0Ids)) {
                const response = await fetchMerchantAppSetupIntentFromUsers();
                if (response.status === 200) {
                    return response.data;
                }

                recoverableError = createResponseError(
                    'Unable to fetch merchant app setup intent',
                    response.status,
                    response.data,
                );
            }
        }
    }

    if (recoverableError) {
        throw recoverableError;
    }

    return null;
};

export const getCurrentAuth0Id = () => {
    const token = localStorage.getItem('access_token');
    const payload = decodeJwtPayload(token);

    return (payload && payload.sub) || null;
};

/**
 * Check if invoice has been paid and advance shipment status if paid
 * @param {string} shipmentId - The shipment ID
 * @returns {Promise} Payment status check result
 */
export const checkPaymentStatus = async (shipmentId) => {
    const buildPaymentReasonMessage = (shipment, billingDetails, invoicingErrors) => {
        const candidateValues = [
            billingDetails && billingDetails.message,
            billingDetails && billingDetails.error_message,
            billingDetails && billingDetails.charge_error,
            invoicingErrors && invoicingErrors.message,
            invoicingErrors && invoicingErrors.error_message,
            invoicingErrors && invoicingErrors.charge_error,
            invoicingErrors && invoicingErrors.error,
            invoicingErrors && invoicingErrors.code,
            billingDetails && billingDetails.code,
        ];

        for (const candidate of candidateValues) {
            if (typeof candidate === 'string' && candidate.trim().length > 0) {
                return candidate.trim();
            }
        }

        if (shipment && shipment.status === 'shipment_saved_pending_payment_retry') {
            return 'Payment failed and needs retry. Please use Pay Invoice once a link is available or update the payment method.';
        }

        return null;
    };

    const resolveInvoiceId = (billingDetails, invoicingErrors) => {
        const isStripeInvoiceId = value =>
            typeof value === 'string' && /^in_[A-Za-z0-9]+$/.test(value.trim());

        const billingCandidates = billingDetails
            ? [
                billingDetails.invoice_id,
                billingDetails.gateway_id,
                billingDetails.stripe_invoice_gateway_id,
                billingDetails.id,
            ]
            : [];
        const invoiceErrorCandidates = invoicingErrors
            ? [
                invoicingErrors.invoice_id,
                invoicingErrors.gateway_id,
                invoicingErrors.stripe_invoice_gateway_id,
                invoicingErrors.id,
            ]
            : [];
        const candidates = [...billingCandidates, ...invoiceErrorCandidates];

        const stripeInvoiceCandidate = candidates.find(isStripeInvoiceId);
        if (stripeInvoiceCandidate) {
            return String(stripeInvoiceCandidate).trim();
        }

        const firstNonEmpty = candidates.find(value => value != null && String(value).trim() !== '');
        return firstNonEmpty ? String(firstNonEmpty).trim() : null;
    };

    const checkPaymentStatusViaFallback = async statusCode => {
        try {
            const hasuraResponse = await axios({
                method: 'post',
                url: getShipmentsHasuraHost(),
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                    'content-type': 'application/json',
                },
                data: {
                    query: `query CheckPaymentStatusFallback($shipmentId: uuid!) {
                          Shipments(where: {shipment_id: {_eq: $shipmentId}}, limit: 1) {
                            shipment_id
                            status
                            billing_details
                            invoicing_errors
                          }
                        }`,
                    variables: { shipmentId },
                },
            });

            const shipment =
                hasuraResponse &&
                hasuraResponse.data &&
                hasuraResponse.data.data &&
                Array.isArray(hasuraResponse.data.data.Shipments) &&
                hasuraResponse.data.data.Shipments.length > 0
                    ? hasuraResponse.data.data.Shipments[0]
                    : null;

            if (!shipment) {
                return {
                    status: 'unavailable',
                    payload: {
                        paid: false,
                        endpointUnsupported: true,
                        shipmentNotFound: true,
                        statusCode,
                    },
                };
            }

            const billingDetails = shipment.billing_details || {};
            const invoicingErrors = shipment.invoicing_errors || null;
            const invoiceId = resolveInvoiceId(billingDetails, invoicingErrors);

            if (invoiceId) {
                try {
                    const invoiceStatusResponse = await axios.get(
                        `${getPaymentsHost()}/v2/invoices/status/${encodeURIComponent(invoiceId)}`,
                        {
                            headers: getAuthHeaders(),
                        }
                    );

                    const invoicePayload =
                        invoiceStatusResponse &&
                        invoiceStatusResponse.data &&
                        invoiceStatusResponse.data.payload
                            ? invoiceStatusResponse.data.payload
                            : null;
                    const invoicePaid = Boolean(invoicePayload && invoicePayload.paid === true);

                    return {
                        status: 'successful',
                        payload: {
                            paid: invoicePaid,
                            source: 'payments-invoice-fallback',
                            shipment_id: shipment.shipment_id,
                            shipment_status: shipment.status,
                            invoice_id: invoiceId,
                            invoice_status: invoicePayload && invoicePayload.status ? invoicePayload.status : null,
                            billing_details: billingDetails,
                            invoicing_errors: invoicingErrors,
                            reasonMessage: invoicePaid
                                ? 'Payment confirmed! Your order is now active.'
                                : (buildPaymentReasonMessage(shipment, billingDetails, invoicingErrors) || 'Invoice is still unpaid. Please use Pay Invoice and then check again.'),
                        },
                    };
                } catch (invoiceStatusError) {
                    void invoiceStatusError;
                }
            }

            const paidByInvoiceFlag = billingDetails && billingDetails.paid === true;
            const pendingRetryStatuses = ['shipment_saved_pending_payment_retry', 'shipment_pending_payment'];
            const paidByShipmentStatus = !pendingRetryStatuses.includes(shipment.status);
            const paid = paidByInvoiceFlag || paidByShipmentStatus;

            return {
                status: 'successful',
                payload: {
                    paid,
                    source: 'hasura-fallback',
                    shipment_id: shipment.shipment_id,
                    shipment_status: shipment.status,
                    billing_details: billingDetails,
                    invoicing_errors: invoicingErrors,
                    invoice_id: invoiceId,
                    reasonMessage: buildPaymentReasonMessage(shipment, billingDetails, invoicingErrors),
                },
            };
        } catch (hasuraError) {
            void hasuraError;

            return {
                status: 'unavailable',
                payload: {
                    paid: false,
                    endpointUnsupported: true,
                    fallbackFailed: true,
                    statusCode,
                },
            };
        }
    };

    if (isKnownAdvanceIfPaidUnsupportedHost() || getKnownAdvanceIfPaidUnsupported()) {
        return checkPaymentStatusViaFallback(404);
    }

    try {
        const response = await axios.put(
            `${getShipmentHost()}/shipments/${shipmentId}/invoice/advance-if-paid`,
            {}, // Empty body - endpoint just checks payment status
            {
                headers: getAuthHeaders()
            }
        );
        return response.data;
    } catch (error) {
        const statusCode = error && error.response ? error.response.status : null;

        // Some deployed environments do not yet expose /invoice/advance-if-paid.
        // Return a structured status so UI can show a useful message instead of failing hard.
        if (statusCode === 404) {
            markAdvanceIfPaidUnsupported();
            return checkPaymentStatusViaFallback(statusCode);
        }

        throw error;
    }
};

/**
 * Extract invoice links from billing details
 * @param {object} shipmentData - Shipment data with billing_details
 * @returns {object|null} Invoice links object with hostedUrl and pdfUrl
 */
export const extractInvoiceLinks = (shipmentData) => {
    if (!shipmentData) return null;

    const billingDetails = shipmentData.billing_details;
    const invoicingErrors = shipmentData.invoicing_errors;
    const candidateSources = [
        shipmentData,
        billingDetails,
        billingDetails && billingDetails.payload,
        billingDetails && billingDetails.data,
        billingDetails && billingDetails.data && billingDetails.data.payload,
        invoicingErrors,
        invoicingErrors && invoicingErrors.payload,
        invoicingErrors && invoicingErrors.data,
        invoicingErrors && invoicingErrors.data && invoicingErrors.data.payload,
    ].filter(Boolean);

    for (const source of candidateSources) {
        const hostedUrl =
            source.hosted_invoice_url ||
            source.hostedUrl ||
            source.invoice_url ||
            source.payment_url ||
            source.invoice_link ||
            source.payment_link ||
            (source.links && (source.links.hosted_invoice_url || source.links.hostedUrl || source.links.invoice_url || source.links.payment_url));

        const pdfUrl =
            source.invoice_pdf ||
            source.invoice_pdf_url ||
            source.pdf_url ||
            (source.links && (source.links.invoice_pdf || source.links.invoice_pdf_url || source.links.pdf_url));

        if (hostedUrl || pdfUrl) {
            return { hostedUrl: hostedUrl || null, pdfUrl: pdfUrl || null };
        }
    }

    return null;
};

/**
 * Extract a displayable invoice identifier from shipment billing details
 * @param {object} shipmentData - Shipment data with billing_details
 * @returns {string|null} Invoice number or identifier for UI labeling
 */
export const extractInvoiceDisplayNumber = (shipmentData) => {
    if (!shipmentData) return null;

    const billingDetails = shipmentData.billing_details;
    const invoicingErrors = shipmentData.invoicing_errors;
    const candidateSources = [
        shipmentData,
        billingDetails,
        billingDetails && billingDetails.payload,
        billingDetails && billingDetails.data,
        billingDetails && billingDetails.data && billingDetails.data.payload,
        invoicingErrors,
        invoicingErrors && invoicingErrors.payload,
        invoicingErrors && invoicingErrors.data,
        invoicingErrors && invoicingErrors.data && invoicingErrors.data.payload,
    ].filter(Boolean);

    for (const source of candidateSources) {
        const candidates = [
            source.invoice_number,
            source.invoiceNumber,
            source.number,
            source.display_number,
            source.invoice_no,
            source.invoice_id,
            source.stripe_invoice_gateway_id,
            source.gateway_id,
            source.id,
            source.links && source.links.invoice_number,
            source.links && source.links.number,
            source.links && source.links.invoice_id,
        ];

        for (const candidate of candidates) {
            if (candidate != null && String(candidate).trim() !== '') {
                return String(candidate).trim();
            }
        }
    }

    return null;
};

/**
 * Get user-friendly error message from billing details
 * @param {object} shipmentData - Shipment data with billing_details
 * @returns {string} User-friendly error message
 */
export const getPaymentErrorMessage = (shipmentData) => {
    if (!shipmentData) return 'Payment processing failed.';

    const billingDetails = shipmentData.billing_details;
    const invoicingErrors = shipmentData.invoicing_errors;
    const candidateSources = [
        shipmentData,
        billingDetails,
        billingDetails && billingDetails.payload,
        billingDetails && billingDetails.data,
        billingDetails && billingDetails.data && billingDetails.data.payload,
        invoicingErrors,
        invoicingErrors && invoicingErrors.payload,
        invoicingErrors && invoicingErrors.data,
        invoicingErrors && invoicingErrors.data && invoicingErrors.data.payload,
    ].filter(Boolean);

    const stringifyIfUseful = value => {
        if (value == null) {
            return '';
        }

        if (typeof value === 'string') {
            return value.trim();
        }

        if (typeof value === 'object') {
            const messageLike = value.message || value.error || value.error_message || value.charge_error || value.detail;
            if (typeof messageLike === 'string') {
                return messageLike.trim();
            }
            return '';
        }

        return String(value).trim();
    };

    for (const source of candidateSources) {
        const candidates = [
            source.message,
            source.error_message,
            source.charge_error,
            source.error,
            source.detail,
            source.reason,
            source.reason_message,
        ];

        for (const candidate of candidates) {
            const parsedMessage = stringifyIfUseful(candidate);
            if (parsedMessage && parsedMessage !== '[object Object]') {
                return parsedMessage;
            }
        }

        if (source.code) {
            const codeText = stringifyIfUseful(source.code);
            if (codeText) {
                return 'Payment failed (' + codeText + '). Please retry payment or use a different card.';
            }
        }
    }

    return 'Payment processing failed. Please complete payment to activate your order.';
};

/**
 * Fetch user's payment profile with saved cards
 * @returns {Promise} Payment profile data with cards array, or null on error
 */
export const fetchPaymentProfile = async (options = {}) => {
    const auth0Ids = getCandidateAuth0Ids(options);
    const currentAuth0Id = getCurrentAuth0Id();

    logRecoverableLookupDebug('[FetchProfile] fetchPaymentProfile called, auth0Ids:', auth0Ids, 'paymentsHost:', getPaymentsHost());

    if (auth0Ids.length === 0) {
        logRecoverableLookupDebug('[FetchProfile] No auth0Ids, returning null');
        return null;
    }

    let recoverableError = null;
    const shouldIncludeCustomerPaymentMethods = options.includeCustomerPaymentMethods !== false;
    const shouldUseCustomerProxyFallback = options.customerProfileProxyFallback !== false;
    const shouldPreferUsersProxy = options.preferUsersProxy === true || (
        options.disableUsersProxyPreference !== true
        && shouldPreferUsersProxyForPayments()
    );
    const shouldProvisionMerchantProfile = options.provisionMerchantProfile !== false;
    const shouldUseCurrentUserProfileFallback = options.currentUserProfileFallback !== false;
    const shouldSkipDirectPaymentsFallback = shouldPreferUsersProxy;

    if (
        shouldPreferUsersProxy
        && shouldUseCurrentUserProfileFallback
        && auth0Ids.length === 1
        && currentAuth0Id
        && auth0Ids[0] === currentAuth0Id
    ) {
        try {
            const response = await fetchCurrentUserPaymentProfileFromUsers();
            const normalizedCurrentUserProfile = normalizePaymentProfileResponse(response.data);

            if (hasPaymentMethods(normalizedCurrentUserProfile)) {
                return withResolvedAuth0Id(normalizedCurrentUserProfile, currentAuth0Id);
            }

            try {
                const directCustomerProfile = await fetchCustomerPaymentProfile(currentAuth0Id, {
                    preferUsersProxy: false,
                    useUsersProxyFallback: false,
                    useMerchantProxyFallback: false,
                });

                if (directCustomerProfile && directCustomerProfile.data) {
                    const normalizedDirectCustomerProfile = normalizePaymentProfileResponse(directCustomerProfile.data);
                    if (hasPaymentMethods(normalizedDirectCustomerProfile)) {
                        return withResolvedAuth0Id(normalizedDirectCustomerProfile, currentAuth0Id);
                    }
                }
            } catch (directFallbackError) {
                if (!isRecoverablePaymentLookupError(directFallbackError)) {
                    throw directFallbackError;
                }
            }
        } catch (fallbackError) {
            if (!isRecoverablePaymentLookupError(fallbackError)) {
                throw fallbackError;
            }
        }
    }

    for (const auth0Id of auth0Ids) {
        const tryUsersProxyProfile = async () => {
            const userProxyResponse = await fetchMerchantPaymentProfileFromUsers(auth0Id);
            logRecoverableLookupDebug('[FetchProfile] Users-proxy SUCCESS for', auth0Id, 'payment_methods:', userProxyResponse.data && userProxyResponse.data.payload && Array.isArray(userProxyResponse.data.payload.payment_methods) ? userProxyResponse.data.payload.payment_methods.length : 'n/a');
            const enrichedProfile = await enrichMerchantProfileWithCustomerPaymentMethods(userProxyResponse.data, auth0Id, {
                includeCustomerPaymentMethods: shouldIncludeCustomerPaymentMethods,
                customerProfileProxyFallback: shouldUseCustomerProxyFallback,
            });
            return withResolvedAuth0Id(enrichedProfile, auth0Id);
        };

        if (shouldPreferUsersProxy) {
            try {
                logRecoverableLookupDebug('[FetchProfile] Prefer users-proxy for', auth0Id);
                const usersProxyProfile = await tryUsersProxyProfile();

                if (hasPaymentMethods(usersProxyProfile)) {
                    return usersProxyProfile;
                }

                try {
                    const directCustomerProfile = await fetchCustomerPaymentProfile(auth0Id, {
                        preferUsersProxy: false,
                        useUsersProxyFallback: false,
                        useMerchantProxyFallback: false,
                    });

                    if (directCustomerProfile && directCustomerProfile.data) {
                        const mergedProfile = mergePaymentProfileResponses(usersProxyProfile, directCustomerProfile.data);
                        if (hasPaymentMethods(mergedProfile)) {
                            return withResolvedAuth0Id(mergedProfile, auth0Id);
                        }
                    }
                } catch (directFallbackError) {
                    if (!isRecoverablePaymentLookupError(directFallbackError)) {
                        throw directFallbackError;
                    }
                }

                return usersProxyProfile;
            } catch (userProxyError) {
                const proxyStatus = getLookupErrorStatus(userProxyError);
                logRecoverableLookupDebug('[FetchProfile] Preferred users-proxy FAILED for', auth0Id, ':', proxyStatus);

                if (!isRecoverablePaymentLookupError(userProxyError)) {
                    throw userProxyError;
                }

                recoverableError = userProxyError;

                if (shouldSkipDirectPaymentsFallback) {
                    continue;
                }
            }
        }

        if (shouldSkipDirectPaymentsFallback) {
            continue;
        }

        logRecoverableLookupDebug('[FetchProfile] Trying auth0Id:', auth0Id, 'direct at:', getCanonicalPaymentsHost() + '/v2/direct/merchant-profiles/' + auth0Id);
        try {
            const response = await axios.get(
                `${getCanonicalPaymentsHost()}/v2/direct/merchant-profiles/${encodeURIComponent(auth0Id)}`,
                {
                    headers: getAuthHeaders(),
                    validateStatus: status => status === 200 || status === 400 || status === 401 || status === 403 || status === 404 || status === 500,
                }
            );

            if (response.status !== 200) {
                throw createResponseError(`Direct merchant profile lookup failed with status ${response.status}`, response.status, response.data);
            }

            const enrichedProfile = await enrichMerchantProfileWithCustomerPaymentMethods(response.data, auth0Id, {
                includeCustomerPaymentMethods: shouldIncludeCustomerPaymentMethods,
                customerProfileProxyFallback: shouldUseCustomerProxyFallback,
            });
            return withResolvedAuth0Id(enrichedProfile, auth0Id);
        } catch (error) {
            var directStatus = getLookupErrorStatus(error);
            logRecoverableLookupDebug('[FetchProfile] Direct call failed for', auth0Id, ':', directStatus);

            if (isRecoverablePaymentLookupError(error)) {
                recoverableError = error;

                if (shouldPreferUsersProxy) {
                    continue;
                }

                logRecoverableLookupDebug('[FetchProfile] Trying users-proxy for', auth0Id);
                try {
                    return await tryUsersProxyProfile();
                } catch (userProxyError) {
                    var proxyStatus = getLookupErrorStatus(userProxyError);
                    logRecoverableLookupDebug('[FetchProfile] Users-proxy FAILED for', auth0Id, ':', proxyStatus);

                    if (!isRecoverablePaymentLookupError(userProxyError)) {
                        throw userProxyError;
                    }

                    recoverableError = userProxyError;
                    continue;
                }
            }

            throw error;
        }
    }

    if (shouldProvisionMerchantProfile && auth0Ids.length === 1 && !shouldSkipDirectPaymentsFallback) {
        try {
            await ensureMerchantPaymentProfile({
                ...options,
                auth0Id: auth0Ids[0],
            });

            const retryResponse = await axios.get(
                `${getCanonicalPaymentsHost()}/v2/direct/merchant-profiles/${encodeURIComponent(auth0Ids[0])}`,
                {
                    headers: getAuthHeaders()
                }
            );

            const enrichedProfile = await enrichMerchantProfileWithCustomerPaymentMethods(retryResponse.data, auth0Ids[0], {
                includeCustomerPaymentMethods: shouldIncludeCustomerPaymentMethods,
                customerProfileProxyFallback: shouldUseCustomerProxyFallback,
            });
            return withResolvedAuth0Id(enrichedProfile, auth0Ids[0]);
        } catch (provisionError) {
            void provisionError;
        }
    }

    if (shouldUseCurrentUserProfileFallback && shouldUseCurrentUserFallback(auth0Ids)) {
        try {
            const response = await fetchCurrentUserPaymentProfileFromUsers();
            return withResolvedAuth0Id(normalizePaymentProfileResponse(response.data), getCurrentAuth0Id());
        } catch (fallbackError) {
            void fallbackError;
            return null;
        }
    }

    if (recoverableError) {
        void recoverableError;
    }

    return null;
};
